/**
 * Keyboard-shortcut reference overlay for the endopod (endoscopy)
 * viewer.  Opens on `?` and lists every binding that is actually
 * wired in the code today — no aspirational entries.
 *
 * If you add or remove a shortcut anywhere in the viewer, mirror the
 * change here so the reference stays trustworthy.  Bindings live in
 *
 *   - specialty/EndoscopyShell.tsx   (video playback + lesion tools)
 *   - viewport/AnnotationLayer.tsx   (annotation submit / cancel)
 *   - app/EndoscopyReadingPage.tsx   (Endoscopy reading-room worklist)
 *   - app/TechnologistPage.tsx       (Bench QA)
 *   - this file                      (`?` to toggle, `Esc` to close)
 */
import { createSignal, onCleanup, onMount, Show, For } from "solid-js";

const GROUPS: { title: string; rows: { keys: string[]; desc: string }[] }[] = [
  {
    title: "Endoscopy viewer",
    rows: [
      { keys: ["Space"],            desc: "Play / pause" },
      { keys: ["←", "→"],           desc: "Step one frame (≈1/30 s) back / forward" },
      { keys: ["Shift", "←", "→"],  desc: "Step one second back / forward" },
      { keys: ["1"],                desc: "Speed 0.5×" },
      { keys: ["2"],                desc: "Speed 1×" },
      { keys: ["4"],                desc: "Speed 2×" },
      { keys: ["A"],                desc: "Anatomy mode" },
      { keys: ["L"],                desc: "Lesion mode" },
      { keys: ["C"],                desc: "Capture still" },
      { keys: ["R"],                desc: "Rectangle ROI (lesion mode)" },
      { keys: ["."],                desc: "Point marker (lesion mode)" },
      { keys: ["Esc"],              desc: "Clear range selection / cancel drawing" },
    ],
  },
  {
    title: "Annotations",
    rows: [
      { keys: ["Enter"],            desc: "Confirm current draw" },
      { keys: ["Esc"],              desc: "Cancel current draw" },
      { keys: ["Del"],              desc: "Delete selected annotation" },
      { keys: ["Backspace"],        desc: "Delete selected annotation" },
    ],
  },
  {
    title: "Reading room (worklist)",
    rows: [
      { keys: ["j"],                desc: "Focus down" },
      { keys: ["k"],                desc: "Focus up" },
      { keys: ["m"],                desc: "Filter: assigned to me" },
      { keys: ["u"],                desc: "Filter: unread" },
      { keys: ["s"],                desc: "Filter: STAT only" },
      { keys: ["a"],                desc: "Filter: all" },
      { keys: ["Enter"],            desc: "Open focused study" },
    ],
  },
  {
    title: "Bench (technologist QA)",
    rows: [
      { keys: ["j"],                desc: "Focus down" },
      { keys: ["k"],                desc: "Focus up" },
      { keys: ["a"],                desc: "Accept study" },
      { keys: ["r"],                desc: "Reject study" },
    ],
  },
  {
    title: "General",
    rows: [
      { keys: ["?"],                desc: "Show / hide this help" },
      { keys: ["Esc"],              desc: "Close panels / cancel tools" },
    ],
  },
];

const [visible, setVisible] = createSignal(false);
export function showShortcuts() { setVisible(true); }
export function toggleShortcuts() { setVisible((v) => !v); }

export function ShortcutHelp() {
  onMount(() => {
    const kd = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "?") { e.preventDefault(); toggleShortcuts(); }
      else if (e.key === "Escape" && visible()) { setVisible(false); }
    };
    window.addEventListener("keydown", kd);
    onCleanup(() => window.removeEventListener("keydown", kd));
  });

  return (
    <Show when={visible()}>
      <div class="fixed inset-0 z-50 grid place-items-center bg-black/70 p-6"
           onClick={() => setVisible(false)}>
        <div class="max-h-[85vh] w-full max-w-4xl overflow-auto rounded-lg border border-ink-300
                    bg-ink-100 p-6 shadow-panel"
             onClick={(e) => e.stopPropagation()}>
          <div class="mb-4 flex items-center justify-between">
            <div class="text-lg font-semibold">Keyboard shortcuts — endoscopy</div>
            <button class="btn-ghost text-xs" onClick={() => setVisible(false)}>Close (Esc)</button>
          </div>
          <div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <For each={GROUPS}>{(g) => (
              <div>
                <div class="mb-2 text-[11px] uppercase tracking-wider text-ink-700">{g.title}</div>
                <ul class="space-y-1">
                  <For each={g.rows}>{(r) => (
                    <li class="flex items-center justify-between gap-3 text-xs">
                      <div class="flex items-center gap-1">
                        <For each={r.keys}>{(k, i) => (
                          <>
                            <kbd>{k}</kbd>
                            {i() < r.keys.length - 1 && <span class="text-ink-700">+</span>}
                          </>
                        )}</For>
                      </div>
                      <span class="text-ink-800">{r.desc}</span>
                    </li>
                  )}</For>
                </ul>
              </div>
            )}</For>
          </div>
          <p class="mt-6 text-[11px] text-ink-700">
            Tip: shortcuts ignore input/textarea focus, so typing in a report won't steal keys.
          </p>
        </div>
      </div>
    </Show>
  );
}
